import React, { useEffect, useState, useMemo, useRef } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import { Card, Button, Space, Spin, message, Select, DatePicker } from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import moment from "moment";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import {
  DEFAULT_SUFFIX_OPTIONS,
  DEFAULT_SELECTED_SUFFIXES,
  selectAllDropdownRender,
} from "../utils/suffixFilterUtils";

const { RangePicker } = DatePicker;
const { Option } = Select;

const ORDERS_PER_PAGE = 50;

const formatCurrency = (amount) => {
  const n = Number(amount || 0);
  if (!n) return "-";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
};

/** Totals row: always show numeric values from API (including 0). */
const formatGrandTotalAmount = (v) => {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return "";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

/** Lightweight table for lines - same look as before, much faster than Ant Design Table */
const LinesTable = React.memo(({ lines, grandTotals }) => {
  const data = Array.isArray(lines) ? lines : [];
  const hasFooter =
    grandTotals &&
    typeof grandTotals === "object" &&
    (grandTotals.income != null ||
      grandTotals.expenses != null ||
      grandTotals.pl != null);
  if (data.length === 0 && !hasFooter) return null;
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 12,
          tableLayout: "fixed",
        }}
      >
        <thead>
          <tr>
            <th style={{ width: 60, padding: "8px", backgroundColor: "#daeffe", textAlign: "center", border: "1px solid #e8e8e8" }}>Sr.</th>
            <th style={{ width: 100, padding: "8px", backgroundColor: "#daeffe", textAlign: "left", border: "1px solid #e8e8e8" }}>Code No</th>
            <th style={{ width: 220, padding: "8px", backgroundColor: "#daeffe", textAlign: "left", border: "1px solid #e8e8e8" }}>Location Name</th>
            <th style={{ width: 180, padding: "8px", backgroundColor: "#daeffe", textAlign: "center", border: "1px solid #e8e8e8" }}>Activity Period</th>
            <th style={{ width: 110, padding: "8px", backgroundColor: "#daeffe", textAlign: "right", border: "1px solid #e8e8e8" }}>Income</th>
            <th style={{ width: 110, padding: "8px", backgroundColor: "#daeffe", textAlign: "right", border: "1px solid #e8e8e8" }}>Expenses</th>
            <th style={{ width: 110, padding: "8px", backgroundColor: "#daeffe", textAlign: "right", border: "1px solid #e8e8e8" }}>P/L</th>
            <th style={{ width: 90, padding: "8px", backgroundColor: "#daeffe", textAlign: "right", border: "1px solid #e8e8e8" }}>P/L %</th>
          </tr>
        </thead>
        <tbody>
          {data.map((line, idx) => {
            const fromStr = line?.activityFrom ? moment(line.activityFrom).format("DD-MM-YYYY") : "";
            const toStr = line?.activityTo ? moment(line.activityTo).format("DD-MM-YYYY") : "";
            const activityPeriod = [fromStr, toStr].filter(Boolean).join(" to ") || "-";
            return (
              <tr key={idx}>
                <td style={{ padding: "6px 8px", border: "1px solid #e8e8e8", textAlign: "center" }}>{idx + 1}</td>
                <td style={{ padding: "6px 8px", border: "1px solid #e8e8e8" }}>{line?.codeNo ?? "-"}</td>
                <td style={{ padding: "6px 8px", border: "1px solid #e8e8e8" }}>{line?.locationName ?? "-"}</td>
                <td style={{ padding: "6px 8px", border: "1px solid #e8e8e8", textAlign: "center" }}>{activityPeriod}</td>
                <td style={{ padding: "6px 8px", border: "1px solid #e8e8e8", textAlign: "right" }}>{formatCurrency(line?.income)}</td>
                <td style={{ padding: "6px 8px", border: "1px solid #e8e8e8", textAlign: "right" }}>{formatCurrency(line?.expenses)}</td>
                <td style={{ padding: "6px 8px", border: "1px solid #e8e8e8", textAlign: "right" }}>{formatCurrency(line?.pl)}</td>
                <td style={{ padding: "6px 8px", border: "1px solid #e8e8e8", textAlign: "right" }}>
                  {line?.plPercent == null ? "-" : `${Number(line.plPercent).toFixed(2).replace(/\.00$/, "")}%`}
                </td>
              </tr>
            );
          })}
        </tbody>
        {hasFooter && (
          <tfoot>
            <tr>
              <td
                colSpan={4}
                style={{
                  padding: "8px",
                  border: "1px solid #e8e8e8",
                  textAlign: "center",
                  fontWeight: "bold",
                  backgroundColor: "#f0f9ff",
                }}
              >
                Grand Total
              </td>
              <td
                style={{
                  padding: "8px",
                  border: "1px solid #e8e8e8",
                  textAlign: "right",
                  fontWeight: "bold",
                  backgroundColor: "#f0f9ff",
                }}
              >
                {formatGrandTotalAmount(grandTotals.income)}
              </td>
              <td
                style={{
                  padding: "8px",
                  border: "1px solid #e8e8e8",
                  textAlign: "right",
                  fontWeight: "bold",
                  backgroundColor: "#f0f9ff",
                }}
              >
                {formatGrandTotalAmount(grandTotals.expenses)}
              </td>
              <td
                style={{
                  padding: "8px",
                  border: "1px solid #e8e8e8",
                  textAlign: "right",
                  fontWeight: "bold",
                  backgroundColor: "#f0f9ff",
                }}
              >
                {formatGrandTotalAmount(grandTotals.pl)}
              </td>
              <td
                style={{
                  padding: "8px",
                  border: "1px solid #e8e8e8",
                  textAlign: "right",
                  backgroundColor: "#f0f9ff",
                }}
              />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
});

const BusinessMemorandumReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [selectedParty, setSelectedParty] = useState(undefined);
  const [selectedGroup, setSelectedGroup] = useState(undefined);
  const [orderNoOptions, setOrderNoOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [selectedOrderNo, setSelectedOrderNo] = useState(undefined);
  const [selectedBrand, setSelectedBrand] = useState(undefined);
  const [selectedSuffix, setSelectedSuffix] = useState(DEFAULT_SELECTED_SUFFIXES);
  const [dateRange, setDateRange] = useState(null);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [orderPage, setOrderPage] = useState(1);
  const scrollContainerRef = useRef(null);
  const topOfContentRef = useRef(null);

  // Build query params for API (same as backend: partyName, orderNo, brandName, suffix, startDate, endDate)
  const reportQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("financialYear", financialYear);
    if (selectedParty) params.set("partyName", selectedParty);
    if (selectedOrderNo) params.set("orderNo", selectedOrderNo);
    if (selectedBrand) params.set("brandName", selectedBrand);
    if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0)
      params.set("suffix", selectedSuffix.join(","));
    if (dateRange && dateRange[0])
      params.set("startDate", dateRange[0].format("DD-MM-YYYY"));
    if (dateRange && dateRange[1])
      params.set("endDate", dateRange[1].format("DD-MM-YYYY"));
    return params.toString();
  }, [financialYear, selectedParty, selectedOrderNo, selectedBrand, selectedSuffix, dateRange]);

  const beginRequest = useLatestRequest();

  useEffect(() => {
    const isCurrent = beginRequest();
    const fetchData = async () => {
      setLoading(true);
      try {
        const url = `${apiBaseUrl}/inventoryManagement/getBusinessMemorandumReport${reportQueryParams ? `?${reportQueryParams}` : ""}`;
        const res = await fetch(url, {
          method: "GET",
          credentials: "include",
        });
        const j = await res.json();
        if (!isCurrent()) return;
        if (j.success) {
          const list = j.data || [];
          setOrders(list);

          // When only FY is applied (no party/order/brand/suffix/date), derive dropdown options from full response
          const suffixNarrows =
            Array.isArray(selectedSuffix) &&
            selectedSuffix.length > 0 &&
            selectedSuffix.length < DEFAULT_SUFFIX_OPTIONS.length;
          const hasNarrowingFilters = Boolean(
            selectedParty ||
              selectedOrderNo ||
              selectedBrand ||
              suffixNarrows ||
              (dateRange && dateRange[0]),
          );
          if (!hasNarrowingFilters && list.length > 0) {
            const parties = Array.from(
              new Set(list.map((o) => o?.clientName).filter(Boolean)),
            );
            parties.sort((a, b) => String(a).localeCompare(String(b)));
            setPartyOptions(parties.map((p) => ({ label: p, value: p })));

            const orderNos = Array.from(
              new Set(list.map((o) => o?.OrderNo).filter(Boolean)),
            );
            orderNos.sort((a, b) => String(a).localeCompare(String(b)));
            setOrderNoOptions(orderNos.map((o) => ({ label: o, value: o })));

            const brands = Array.from(
              new Set(list.map((o) => o?.brandName).filter(Boolean)),
            );
            brands.sort((a, b) => String(a).localeCompare(String(b)));
            setBrandOptions(brands.map((b) => ({ label: b, value: b })));
          }
        } else {
          console.error(
            "Failed to fetch New Business Memorandum report:",
            j.message,
          );
          message.error("Failed to fetch report");
        }
      } catch (err) {
        if (!isCurrent()) return;
        console.error("Error fetching New Business Memorandum report:", err);
        message.error("Error fetching report");
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    fetchData();
  }, [apiBaseUrl, reportQueryParams]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("excel", "true");
      if (selectedParty) params.set("partyName", selectedParty);
      // if (selectedGroup) params.set("group", selectedGroup);
      if (selectedOrderNo) params.set("orderNo", selectedOrderNo);
      if (selectedBrand) params.set("brandName", selectedBrand);
      if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0)
        params.set("suffix", selectedSuffix.join(",")); // <-- add
      if (dateRange && dateRange[0])
        params.set("startDate", dateRange[0].format("DD-MM-YYYY"));
      if (dateRange && dateRange[1])
        params.set("endDate", dateRange[1].format("DD-MM-YYYY"));

      const url = `${apiBaseUrl}/inventoryManagement/getBusinessMemorandumReport?${params.toString()}`;
      const res = await fetch(url, { method: "GET", credentials: "include" });
      if (!res.ok) {
        message.error("Failed to download Excel");
        return;
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `Business_Memorandum_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Error downloading Excel:", err);
      message.error("Error downloading Excel");
    } finally {
      setDownloading(false);
    }
  };

  // Orders are already filtered by the API; no client-side filtering
  const totalOrderPages = Math.max(1, Math.ceil((orders?.length || 0) / ORDERS_PER_PAGE));
  const safeOrderPage = Math.min(orderPage, totalOrderPages) || 1;
  const paginatedOrders = useMemo(() => {
    const list = orders || [];
    const start = (safeOrderPage - 1) * ORDERS_PER_PAGE;
    return list.slice(start, start + ORDERS_PER_PAGE);
  }, [orders, safeOrderPage]);

  useEffect(() => {
    setOrderPage(1);
  }, [financialYear, selectedParty, selectedOrderNo, selectedBrand, selectedSuffix, dateRange]);

  useEffect(() => {
    if (orderPage > totalOrderPages && totalOrderPages > 0) setOrderPage(1);
  }, [totalOrderPages, orderPage]);

  const scrollToTop = () => {
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollTop = 0;
      let parent = el.parentElement;
      while (parent) {
        if (parent.scrollHeight > parent.clientHeight) {
          parent.scrollTop = 0;
        }
        parent = parent.parentElement;
      }
    }
    topOfContentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    scrollToTop();
  }, [reportQueryParams]);

  return (
    <div
      ref={scrollContainerRef}
      className="parent-padding-container"
      style={{ backgroundColor: "white", borderRadius: 12, padding: 20 }}
    >
      <Card
        ref={topOfContentRef}
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              Business Memorandum
            </Space>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                padding: "12px 0",
                marginBottom: "4px",
              }}
            >
              <Space wrap size="middle">
                <Select
                  value={financialYear}
                  onChange={setFinancialYear}
                  style={{ width: 120 }}
                  placeholder="Select FY"
                >
                  {getFinancialYearOptions().map((fy) => (
                    <Option key={fy} value={fy}>
                      {fy}
                    </Option>
                  ))}
                </Select>
                <Select
                  placeholder="Order No."
                  allowClear
                  showSearch
                  value={selectedOrderNo}
                  onChange={setSelectedOrderNo}
                  options={orderNoOptions}
                  style={{ width: 180 }}
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? option?.value ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                />
                <Select
                  mode="multiple"
                  placeholder="Filter by Suffix"
                  allowClear
                  value={selectedSuffix.length ? selectedSuffix : undefined}
                  onChange={(val) => setSelectedSuffix(val ?? [])}
                  options={DEFAULT_SUFFIX_OPTIONS}
                  style={{ minWidth: 160 }}
                  dropdownRender={selectAllDropdownRender(
                    DEFAULT_SUFFIX_OPTIONS,
                    selectedSuffix,
                    setSelectedSuffix,
                  )}
                />
                <RangePicker
                  format="DD-MM-YYYY"
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                  placeholder={["From", "To"]}
                  style={{ width: 260 }}
                />
              </Space>
              <Space wrap size="middle">
                <Select
                  placeholder="Search by Brand Name"
                  allowClear
                  showSearch
                  value={selectedBrand}
                  onChange={setSelectedBrand}
                  options={brandOptions}
                  style={{ width: 200 }}
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? option?.value ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                />
                <Select
                  placeholder="Search by Party Name"
                  allowClear
                  showSearch
                  value={selectedParty}
                  onChange={setSelectedParty}
                  options={partyOptions}
                  style={{ width: 260 }}
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? option?.value ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                />
                <Button
                  type="primary"
                  icon={<FaDownload />}
                  loading={downloading}
                  onClick={handleDownloadExcel}
                >
                  Download Excel
                </Button>
              </Space>
            </div>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : !orders || orders.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#999" }}>
            No records found
          </div>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              {paginatedOrders.map((order, orderIdx) => (
                <Card
                  key={`${order.OrderNo || "order"}-${orderIdx}`}
                  size="small"
                  style={{ borderColor: "#e0e0e0" }}
                  bodyStyle={{ padding: 12 }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      rowGap: 4,
                      columnGap: 16,
                      marginBottom: 8,
                      fontSize: 12,
                      justifyItems: "center",
                    }}
                  >
                    <div>
                      <strong>Media Planner:</strong>{" "}
                      <span>{order.mediaPlanner || "-"}</span>
                    </div>
                    <div>
                      <strong>Client Name:</strong>{" "}
                      <span>{order.clientName || "-"}</span>
                    </div>
                    <div>
                      <strong>Industry:</strong>{" "}
                      <span>{order.industry || "-"}</span>
                    </div>
                    <div>
                      <strong>Brand Name:</strong>{" "}
                      <span>{order.brandName || "-"}</span>
                    </div>
                    <div>
                      <strong>Salesman:</strong>{" "}
                      <span>{order.salesman || "-"}</span>
                    </div>
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
                      <span>
                        <strong>Order No.:</strong>{" "}
                        {order.OrderNo || "-"}
                      </span>
                      <span>
                        <strong>Order Date:</strong>{" "}
                        {order.orderDate ? moment(order.orderDate).format("DD-MM-YYYY") : "-"}
                      </span>
                    </div>
                  </div>
                  <LinesTable lines={order.lines} grandTotals={order.grandTotals} />
                </Card>
              ))}
            </div>
            {totalOrderPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
                <Button
                  disabled={safeOrderPage <= 1}
                  onClick={() => {
                    setOrderPage((p) => Math.max(1, p - 1));
                    scrollToTop();
                  }}
                >
                  Previous
                </Button>
                <span style={{ fontSize: 13 }}>
                  Page {safeOrderPage} of {totalOrderPages}
                  {orders?.length != null && (
                    <span style={{ color: "#666", marginLeft: 8 }}>
                      ({orders.length} order{orders.length !== 1 ? "s" : ""})
                    </span>
                  )}
                </span>
                <Button
                  disabled={safeOrderPage >= totalOrderPages}
                  onClick={() => {
                    setOrderPage((p) => Math.min(totalOrderPages, p + 1));
                    scrollToTop();
                  }}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default BusinessMemorandumReport;
